// src/services/mosqueDonationService.ts
import axios from "axios";
import { API_BASE_URL } from "../config";
import { asErr } from "../utils/apiHelpers";
import type { MosqueDonationOpportunityDetail } from "../types/mosqueDonationOpportunity";
import { getAuthToken } from "./authService";

/**
 * Single Axios instance for Mosque Donation APIs.
 * IMPORTANT:
 * - Always use API_BASE_URL so requests never go to the React site origin (e.g., :8081) in dev.
 * - This prevents 406/404 coming from the wrong server (IIS/React instead of Backend API).
 */
const api = axios.create({
  baseURL: API_BASE_URL, // ✅ Always hit backend API host/port
  headers: { Accept: "application/json" },
});

/**
 * Category A options
 */
export async function listCatA() {
  // GET: /api/mosque/donation/categories/a
  const res = await api.get("/api/mosque/donation/categories/a");
  return res.data;
}

/**
 * Category B options
 */
export async function listCatB() {
  // GET: /api/mosque/donation/categories/b
  const res = await api.get("/api/mosque/donation/categories/b");
  return res.data;
}

/**
 * Category C options
 */
export async function listCatC() {
  // GET: /api/mosque/donation/categories/c
  const res = await api.get("/api/mosque/donation/categories/c");
  return res.data;
}

/**
 * Category D options
 */
export async function listCatD() {
  // GET: /api/mosque/donation/categories/d
  const res = await api.get("/api/mosque/donation/categories/d");
  return res.data;
}

/**
 * List donation opportunities with query string filters/paging.
 * Example queryString: "catAId=1&catBId=2&pageNo=1&pageSize=12"
 */
export async function listOpportunities(queryString: string) {
  // GET: /api/donations/mosques/opportunities?{queryString}
  const res = await api.get(`/api/donations/mosques/opportunities?${queryString}`);
  return res.data;
}

// --- Mosque supply request & opportunity APIs (used by MosqueDonationRequest and MosqueInformationDonationPage) ---
const getBaseURL = () =>
  process.env.NODE_ENV === "development" ? "" : API_BASE_URL;

const authApi = () => {
  const client = axios.create({
    baseURL: getBaseURL(),
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    withCredentials: false,
    validateStatus: (s) => s >= 200 && s < 500,
  });
  const tok = getAuthToken();
  if (tok) client.defaults.headers.common.Authorization = `Bearer ${tok}`;
  return client;
};

type MosqueSupplyRequestPayload = {
  DDR_id: number;
  DonorIdToken: string;
  mosque_id: number;
  /** معرف شركة المقاولة (اختياري؛ يُرسل عند اختيار شركة بدون مستفيد) */
  supplier_id?: number;
  /** اسم متعهد الصيانة (اختياري؛ يُرسل مع الطلب عند اختيار متعهد) */
  supplier_name?: string;
  /** تاريخ تنفيذ الصيانة (اختياري؛ YYYY-MM-DD) */
  maintenance_date?: string;
  /** أنا من سينفذ الصيانة — المستخدم هو المنفذ */
  is_self_provider?: boolean;
};

export async function submitMosqueSupplyRequest(
  payload: MosqueSupplyRequestPayload
): Promise<unknown> {
  const body: Record<string, unknown> = {
    DDR_id: payload.DDR_id,
    DonorIdToken: payload.DonorIdToken,
    mosque_id: payload.mosque_id,
  };
  if (payload.DonorIdToken != null && payload.DonorIdToken !== "") body.donorToken = payload.DonorIdToken;
  if (payload.supplier_id != null) body.supplier_id = payload.supplier_id;
  if (payload.supplier_name != null && payload.supplier_name !== "") body.supplier_name = payload.supplier_name;
  if (payload.maintenance_date != null && payload.maintenance_date !== "") body.maintenance_date = payload.maintenance_date;
  if (payload.is_self_provider === true) {
    body.is_self_provider = true;
    body.IsSelfProvider = true;
    body.isSelfProvider = true;
  }
  const supplyClient = axios.create({
    baseURL: API_BASE_URL,
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    withCredentials: false,
    validateStatus: (s) => s >= 200 && s < 500,
  });
  const tok = getAuthToken();
  if (tok) supplyClient.defaults.headers.common.Authorization = `Bearer ${tok}`;
  const res = await supplyClient.post("/api/donations/mosques/supply/request", body);
  if (res.status >= 400) {
    const msg =
      (res.data && (res.data.message || res.data.error)) || `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return res.data;
}

/** Body for PATCH /api/donations/supply/request — تحديث الطلب ببيانات المتعهد والتاريخ ليعكس في النظام */
export type PatchSupplyRequestBody = {
  supply_id: number;
  supplier_id?: number;
  donorToken?: string;
  contract_date?: string;
  contract_duration?: number;
  requested_amount?: number;
  final_amount?: number;
  /** أنا من سينفذ الصيانة — المستخدم هو المنفذ */
  is_self_provider?: boolean;
};

/**
 * PATCH /api/donations/supply/request
 * يحدّث طلب الصيانة ببيانات متعهد الصيانة والتاريخ حتى تظهر في النظام (Donor & Supplier).
 */
export async function patchSupplyRequest(
  body: PatchSupplyRequestBody
): Promise<unknown> {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    withCredentials: false,
    validateStatus: (s) => s >= 200 && s < 500,
  });
  const tok = getAuthToken();
  if (tok) client.defaults.headers.common.Authorization = `Bearer ${tok}`;
  const payload: Record<string, unknown> = {
    supply_id: body.supply_id,
    supplyId: body.supply_id,
  };
  if (body.supplier_id != null) {
    payload.supplier_id = body.supplier_id;
    payload.SupplierId = body.supplier_id;
  }
  if (body.donorToken != null && body.donorToken !== "") {
    payload.donorToken = body.donorToken;
    payload.DonorIdToken = body.donorToken;
    payload.ContributorToken = body.donorToken;
    payload.contributorToken = body.donorToken;
  }
  if (body.contract_date != null && body.contract_date !== "") payload.contract_date = body.contract_date;
  if (body.contract_duration != null) payload.contract_duration = body.contract_duration;
  if (body.requested_amount != null) payload.requested_amount = body.requested_amount;
  if (body.final_amount != null) payload.final_amount = body.final_amount;
  if (body.is_self_provider === true) {
    payload.is_self_provider = true;
    payload.IsSelfProvider = true;
    payload.isSelfProvider = true;
  }
  const res = await client.patch("/api/donations/supply/request", payload);
  if (res.status >= 400) {
    const msg =
      (res.data && (res.data.message || res.data.error)) || `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return res.data;
}

export function getVal(obj: any, ...keys: string[]): any {
  if (obj == null) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

export function flattenDetailPayload(row: any): any {
  if (row == null || typeof row !== "object") return row ?? {};
  const out = { ...row };
  const nestedKeys = ["building", "status", "mosqueStatus", "toilets", "electricity", "sound", "details", "mosque", "opportunity", "categories", "Categories"];
  for (const key of nestedKeys) {
    const child = row[key];
    if (child != null && typeof child === "object" && !Array.isArray(child)) {
      Object.assign(out, child);
      // دمج mosque داخل opportunity أو details (بنية متداخلة من الـ API)
      const inner = child.mosque ?? child.Mosque ?? child.masjid ?? child.Masjid;
      if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
        Object.assign(out, inner);
      }
    }
  }
  // استخراج أسماء الفئات من كائنات CatA/CatB... (CatA: { Name: "..." })
  const catExtract: Array<{ objKeys: string[]; outKey: string }> = [
    { objKeys: ["catA", "CatA", "cat_a", "Cat_A"], outKey: "Cat_A_Name" },
    { objKeys: ["catB", "CatB", "cat_b", "Cat_B"], outKey: "Cat_B_Name" },
    { objKeys: ["catC", "CatC", "cat_c", "Cat_C"], outKey: "Cat_C_Name" },
    { objKeys: ["catD", "CatD", "cat_d", "Cat_D"], outKey: "Cat_D_Name" },
    { objKeys: ["catE", "CatE", "cat_e", "Cat_E"], outKey: "Cat_E_Names" },
  ];
  for (const { objKeys, outKey } of catExtract) {
    for (const ok of objKeys) {
      const obj = out[ok];
      if (obj != null && typeof obj === "object" && !Array.isArray(obj)) {
        const n = obj.Name ?? obj.name;
        if (n != null && n !== "") {
          out[outKey] = n;
          const snake = outKey.replace(/_Name(s)?$/, "_name$1").replace(/Cat_/g, "cat_");
          out[snake] = n;
          break;
        }
      }
    }
  }
  return out;
}

const mapDetail = (r: any): MosqueDonationOpportunityDetail => {
  const raw = flattenDetailPayload(r);
  const g = (...keys: string[]) => getVal(raw, ...keys);
  const num = (v: any) => {
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  return {
    requestId: Number(g("Request_Id", "request_Id", "request_id") ?? 0),
    requestName: g("Request_Name", "request_Name", "request_name") ?? null,
    mosqueId: Number(g("Mosque_Id", "mosque_Id", "mosque_id") ?? 0),
    number: g("Number", "number") ?? null,
    mosqueName: g("Mosque_Name", "mosque_Name", "mosque_name") ?? null,
    latitude: num(g("Latitude", "latitude")),
    longitude: num(g("Longitude", "longitude")),
    classificationId: g("Classification_Id", "classification_Id", "classification_id") ?? null,
    classificationName: g("Classification_Name", "classification_Name", "classification_name") ?? null,
    regionName: g("Region_Name", "region_Name", "region_name") ?? null,
    cityName: g("City_Name", "city_Name", "city_name") ?? null,
    moiaCenterName: g("Moia_Center_Name", "moia_Center_Name", "moia_center_name") ?? null,
    fullAddress: g("Full_Address", "full_Address", "full_address") ?? null,
    districtName: g("District_Name", "district_Name", "district_name") ?? null,
    catAId: g("Cat_A_Id", "cat_A_Id", "cat_a_id") ?? null,
    catAName: g("Cat_A_Name", "cat_A_Name", "cat_a_name") ?? null,
    catBId: g("Cat_B_Id", "cat_B_Id", "cat_b_id") ?? null,
    catBName: g("Cat_B_Name", "cat_B_Name", "cat_b_name") ?? null,
    catCId: g("Cat_C_Id", "cat_C_Id", "cat_c_id") ?? null,
    catCName: g("Cat_C_Name", "cat_C_Name", "cat_c_name") ?? null,
    catDId: g("Cat_D_Id", "cat_D_Id", "cat_d_id") ?? null,
    catDName: g("Cat_D_Name", "cat_D_Name", "cat_d_name") ?? null,
    mosqueOpeningDate: g("Mosque_Opening_Date", "mosque_Opening_Date", "mosque_opening_date") ?? null,
    urbanCondition: g("Urban_Condition", "urban_Condition", "urban_condition") ?? null,
    dateMaintenanceLast: g("Date_Maintenance_Last", "date_Maintenance_Last", "date_maintenance_last") ?? null,
    fridayPrayerRows: num(g("Friday_Prayer_Rows", "friday_Prayer_Rows", "friday_prayer_rows")),
    surface: g("Surface", "surface") ?? null,
    landArea: g("Land_Area", "land_Area", "land_area") ?? null,
    lengthRowMenPraying: g("Length_Row_Men_Praying", "length_Row_Men_Praying", "length_row_men_praying") ?? null,
    rowMenPrayingNumber: num(g("Row_Men_Praying_Number", "row_Men_Praying_Number", "row_men_praying_number")),
    toiletMenNumber: num(g("Toilet_Men_Number", "toilet_Men_Number", "toilet_men_number")),
    menPrayerAvgAttendance: num(g("Men_Prayer_Avg_Attendance", "men_Prayer_Avg_Attendance", "men_prayer_avg_attendance")),
    lengthRowWomenPraying: g("Length_Row_Women_Praying", "length_Row_Women_Praying", "length_row_women_praying") ?? null,
    rowWomenPrayingNumber: num(g("Row_Women_Praying_Number", "row_Women_Praying_Number", "row_women_praying_number")),
    womenPrayerRoomCapacity: num(g("Women_Prayer_Room_Capacity", "women_Prayer_Room_Capacity", "women_prayer_room_capacity")),
    isWomenToilets: g("Is_Women_Toilets", "is_Women_Toilets", "is_women_toilets") ?? null,
    toiletWomanNumber: num(g("Toilet_Woman_Number", "toilet_Woman_Number", "toilet_woman_number")),
    internalDoorsNumber: num(g("Internal_Doors_Number", "internal_Doors_Number", "internal_doors_number")),
    internalHeadsetNumber: num(g("Internal_Headset_Number", "internal_Headset_Number", "internal_headset_number")),
    internalSpeakerNumber: num(g("Internal_Speaker_Number", "internal_Speaker_Number", "internal_speaker_number")),
    hasExternalCamera: g("Has_External_Camera", "has_External_Camera", "has_external_camera") ?? null,
    hasInternalCamera: g("Has_Internal_Camera", "has_Internal_Camera", "has_internal_camera") ?? null,
    externalDoorsNumbers: num(g("External_Doors_Numbers", "external_Doors_Numbers", "external_doors_numbers")),
    externalHeadsetNumber: num(g("External_Headset_Number", "external_Headset_Number", "external_headset_number")),
    externalSpeakerNumber: num(g("External_Speaker_Number", "external_Speaker_Number", "external_speaker_number")),
    numMinarets: num(g("Num_Minarets", "num_Minarets", "num_minarets")),
    numFloors: num(g("Num_Floors", "num_Floors", "num_floors")),
    hasBasement: g("Has_Basement", "has_Basement", "has_basement") ?? null,
    mosqueRooms: num(g("Mosque_Rooms", "mosque_Rooms", "mosque_rooms")),
    nonBuildingArea: g("Non_Building_Area", "non_Building_Area", "non_building_area") ?? null,
    roofedArea: g("Roofed_Area", "roofed_Area", "roofed_area") ?? null,
    isFreeArea: g("Is_Free_Area", "is_Free_Area", "is_free_area") ?? null,
    hasAirConditioners: g("Has_Air_Conditioners", "has_Air_Conditioners", "has_air_conditioners") ?? null,
    acType: g("Ac_Type", "ac_Type", "ac_type") ?? null,
    numAirConditioners: num(g("Num_Air_Conditioners", "num_Air_Conditioners", "num_air_conditioners")),
    numLightingInside: num(g("Num_Lighting_Inside", "num_Lighting_Inside", "num_lighting_inside")),
    hasFireExtinguishers: g("Has_Fire_Extinguishers", "has_Fire_Extinguishers", "has_fire_extinguishers") ?? null,
    hasFireSystemPumps: g("Has_Fire_System_Pumps", "has_Fire_System_Pumps", "has_fire_system_pumps") ?? null,
    mosqueHistorical: g("Mosque_Historical", "mosque_Historical", "mosque_historical") ?? null,
    catEIds: Array.isArray(raw?.Cat_E_Ids) ? raw.Cat_E_Ids.map((x: any) => Number(x)) : (Array.isArray(raw?.cat_e_ids) ? raw.cat_e_ids.map((x: any) => Number(x)) : null),
    catENames: g("Cat_E_Names", "cat_E_Names", "cat_e_Names", "cat_e_names") ?? null,
  };
};

function unwrapOpportunityPayload(res: any): any {
  const d = res?.data;
  if (!d) return res;
  const candidates = [d?.data, Array.isArray(d?.data) ? d.data[0] : undefined, d?.result, Array.isArray(d?.result) ? d.result[0] : undefined, d?.response, d].filter(Boolean);
  for (const c of candidates) {
    const obj = typeof c === "object" && c !== null ? c : null;
    if (obj && (obj.request_id != null || obj.Request_Id != null || obj.mosque_id != null || obj.Mosque_Id != null || obj.mosque_Name != null))
      return obj;
  }
  return Array.isArray(d) ? d[0] : d;
}

export async function getMosqueDonationOpportunityByRequestId(
  requestId: number
): Promise<MosqueDonationOpportunityDetail> {
  const url = `/api/donations/mosques/opportunities/${requestId}`;
  let res = await authApi().get(url);

  if (res.status >= 400) {
    const msg = (res.data && (res.data.message || res.data.error)) || `Request failed (${res.status})`;
    throw asErr("Failed to load opportunity details", new Error(msg));
  }
  const row = unwrapOpportunityPayload(res);
  return mapDetail(row ?? res?.data ?? {});
}

/**
 * GET /api/mosques/images?masjid_id={masjidId}
 * [Authorize] — يتطلب تسجيل الدخول (Bearer token).
 * الاستجابة من الخادم: { success, emasjid: { id, inner_Image (Base64), outer_Image (Base64) } }
 */
export type MosqueImagesResult = {
  internalUrls: string[];
  externalUrls: string[];
};

/** يحوّل قيمة صورة (رابط أو Base64) إلى src جاهز لعرضها في <img> */
function toImageSrc(v: unknown): string | null {
  if (v == null) return null;
  const s = typeof v === "string" ? v.trim() : "";
  if (!s) return null;
  if (s.startsWith("data:") || s.startsWith("http://") || s.startsWith("https://")) return s;
  return `data:image/jpeg;base64,${s}`;
}

function toImageSrcList(v: unknown): string[] {
  const src = toImageSrc(v);
  if (src) return [src];
  if (Array.isArray(v)) {
    return v.map((x) => toImageSrc(x)).filter((x): x is string => x != null);
  }
  return [];
}

export async function getMosqueImages(masjidId: number): Promise<MosqueImagesResult> {
  if (masjidId == null || masjidId <= 0) {
    throw new Error("masjid_id is required.");
  }
  const client = authApi();
  const res = await client.get("/api/mosques/images", {
    params: { masjid_id: masjidId },
    timeout: 30000,
  });
  if (res.status >= 400) {
    const msg =
      (res.data && typeof res.data === "object" && (res.data as any).message) ||
      `Request failed (${res.status})`;
    throw new Error(msg);
  }
  const raw = res.data != null && typeof res.data === "object" ? res.data : {};
  const r = raw as Record<string, unknown>;
  const emasjid = (r.emasjid ?? r.Emasjid ?? (r.data as any)?.emasjid) as Record<string, unknown> | null | undefined;
  const internalUrls = emasjid
    ? toImageSrcList(
        emasjid.inner_Image ?? emasjid.Inner_Image ?? emasjid.innerImage ?? emasjid.InnerImage
      )
    : toImageSrcList(r.internal ?? r.internalImage ?? r.Internal ?? r.InternalImage);
  const externalUrls = emasjid
    ? toImageSrcList(
        emasjid.outer_Image ?? emasjid.Outer_Image ?? emasjid.outerImage ?? emasjid.OuterImage
      )
    : toImageSrcList(r.external ?? r.externalImage ?? r.External ?? r.ExternalImage);
  return { internalUrls, externalUrls };
}

export type SupplyRequestListItem = {
  requestId: number;
  requestName: string | null;
  mosqueId: number;
  /** معرّف الفرصة (DDR) لربط زر العرض ببيانات المسجد */
  ddrId?: number | null;
  mosqueName: string | null;
  number: string | null;
  regionName: string | null;
  cityName: string | null;
  status: string | null;
  supplyId: number;
  supplyName: string | null;
  supplyDate: string | null;
  stageId: number | null;
  state: string | null;
  partnerName: string | null;
  requestedAmount: number | null;
  finalAmount: number | null;
};

function mapSupplyRequestRow(r: any): SupplyRequestListItem {
  const supplyId = Number(r?.supplyId ?? r?.SupplyId ?? r?.Request_Id ?? r?.request_id ?? r?.Id ?? r?.id ?? 0);
  const supplyName = r?.supplyName ?? r?.SupplyName ?? r?.Request_Name ?? r?.request_name ?? r?.RequestNumber ?? r?.request_number ?? null;
  const mosqueNumber = r?.mosqueNumber ?? r?.MosqueNumber ?? r?.Number ?? r?.number ?? null;
  const mosqueName = r?.mosqueName ?? r?.Mosque_Name ?? r?.mosque_name ?? null;
  const state = r?.state ?? r?.State ?? r?.Status ?? r?.status ?? r?.StatusName ?? r?.status_name ?? null;
  return {
    requestId: supplyId,
    requestName: supplyName,
    mosqueId: Number(r?.mosqueId ?? r?.Mosque_Id ?? r?.mosque_id ?? 0),
    mosqueName: mosqueName ?? null,
    number: mosqueNumber ?? null,
    regionName: r?.regionName ?? r?.Region_Name ?? r?.region_name ?? null,
    cityName: r?.cityName ?? r?.City_Name ?? r?.city_name ?? null,
    status: state,
    supplyId,
    supplyName: supplyName ?? null,
    supplyDate: r?.supplyDate ?? r?.SupplyDate ?? null,
    stageId: r?.stageId != null ? Number(r.stageId) : r?.StageId != null ? Number(r.StageId) : null,
    state: state ?? null,
    partnerName: r?.partnerName ?? r?.PartnerName ?? null,
    requestedAmount: typeof r?.requestedAmount === "number" ? r.requestedAmount : r?.RequestedAmount != null ? Number(r.RequestedAmount) : null,
    finalAmount: typeof r?.finalAmount === "number" ? r.finalAmount : r?.FinalAmount != null ? Number(r.FinalAmount) : null,
    // معرّف الفرصة (DDR) — يُستخرج من أي من الحقول التالية إن وُجد في استجابة API
    ddrId: (() => {
      const v = Number(
        r?.ddr_id ?? r?.ddrId ?? r?.DDR_Id ?? r?.DDRID ?? r?.DdrId ??
        r?.opportunity_id ?? r?.opportunityId ?? r?.Opportunity_Id ?? r?.OpportunityId ??
        r?.DonationRequestId ?? r?.DonationRequest_Id ?? r?.donation_request_id ?? 0
      );
      return v > 0 ? v : null;
    })(),
  };
}

export type ListSupplyRequestsParams = {
  DonorIdToken: string;
  PageNo?: number;
  PageSize?: number;
};

export type ListSupplyRequestsResult = {
  pageNo: number;
  pageSize: number;
  count: number;
  data: SupplyRequestListItem[];
};

function parseMaybeJson(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function looksLikeSupplyRequestRow(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const row = value as Record<string, unknown>;
  return Boolean(
    row.supplyId ?? row.SupplyId ?? row.request_id ?? row.Request_Id ??
    row.request_name ?? row.Request_Name ?? row.supplyName ?? row.SupplyName ??
    row.mosqueName ?? row.mosque_name ?? row.partnerName ?? row.PartnerName
  );
}

function findSupplyRequestRowsDeep(value: unknown, depth = 0): any[] {
  const current = parseMaybeJson(value);
  if (depth > 6 || current == null) return [];
  if (Array.isArray(current)) return current;
  if (looksLikeSupplyRequestRow(current)) return [current];
  if (typeof current !== "object") return [];

  const obj = current as Record<string, unknown>;
  const preferredKeys = [
    "data", "Data", "items", "Items", "rows", "Rows", "records", "Records",
    "result", "Result", "payload", "Payload", "response", "Response",
  ];

  for (const key of preferredKeys) {
    if (!(key in obj)) continue;
    const nested = findSupplyRequestRowsDeep(obj[key], depth + 1);
    if (nested.length > 0) return nested;
  }

  for (const nestedValue of Object.values(obj)) {
    const nested = findSupplyRequestRowsDeep(nestedValue, depth + 1);
    if (nested.length > 0) return nested;
  }

  return [];
}

function unwrapSupplyRequestListPayload(value: unknown): { parsed: any; rows: any[] } {
  let current: any = parseMaybeJson(value);

  for (let depth = 0; depth < 6; depth++) {
    current = parseMaybeJson(current);

    if (Array.isArray(current)) {
      return { parsed: current, rows: current };
    }

    if (!current || typeof current !== "object") {
      return { parsed: current, rows: [] };
    }

    const directRows =
      current?.data ?? current?.Data ?? current?.items ?? current?.Items ??
      current?.rows ?? current?.Rows ?? current?.records ?? current?.Records ??
      current?.result ?? current?.Result ?? current?.payload ?? current?.Payload ??
      current?.response ?? current?.Response;

    if (Array.isArray(directRows)) {
      return { parsed: current, rows: directRows };
    }

    const next = parseMaybeJson(directRows);
    if (next === undefined || next === null || next === current) {
      const deepRows = findSupplyRequestRowsDeep(current, depth);
      return { parsed: current, rows: deepRows };
    }

    current = next;
  }

  const deepRows = findSupplyRequestRowsDeep(current);
  return { parsed: current, rows: deepRows };
}

export async function listSupplyRequestsByDonor(
  params: ListSupplyRequestsParams
): Promise<ListSupplyRequestsResult> {
  const body = {
    DonorIdToken: params.DonorIdToken,
    PageNo: params.PageNo ?? 1,
    PageSize: params.PageSize ?? 50,
  };
  const res = await authApi().post("/api/donation/supply/request/list", body);
  if (res.status >= 400) {
    const msg =
      (res.data && (res.data.message || res.data.error)) || `Request failed (${res.status})`;
    throw new Error(msg);
  }
  const { parsed, rows } = unwrapSupplyRequestListPayload(res.data);
  const arr = Array.isArray(rows) ? rows : [];
  const data = arr.map((r: any) => mapSupplyRequestRow(r));
  // في وضع التطوير: إذا وُجدت صفوف بدون ddrId، اطبع عينة للمساعدة في تصحيح API
  if (process.env.NODE_ENV === "development") {
    const withoutDdr = data.find((d) => !d.ddrId);
    if (withoutDdr) {
      const sampleRaw = arr.find((r: any) => {
        const v = Number(r?.ddr_id ?? r?.ddrId ?? r?.DDR_Id ?? r?.opportunity_id ?? 0);
        return v <= 0;
      });
      if (sampleRaw) {
        console.debug("[mosqueDonationService] صف بدون ddrId — عينة من استجابة API:", Object.keys(sampleRaw), sampleRaw);
      }
    }
    if (data.length === 0) {
      console.debug("[mosqueDonationService] listSupplyRequestsByDonor parsed empty payload:", parsed);
    }
  }
  return {
    pageNo: Number(parsed?.pageNo ?? parsed?.PageNo ?? 1),
    pageSize: Number(parsed?.pageSize ?? parsed?.PageSize ?? body.PageSize),
    count: Number(parsed?.count ?? parsed?.Count ?? data.length),
    data,
  };
}


// NEW: raw JSON endpoint
// NEW: raw JSON endpoint
export async function listOpportunitiesJson(queryString: string) {
  const url = `/api/donations/mosques/opportunities/json${queryString ? `?${queryString}` : ""}`;
  const res = await api.get(url);   // <-- replace "api" with your existing client name
  return res.data;
}
